<template>
  <section v-if="ticket" class="incall-origin-ticket">
    <header class="incall-origin-ticket__header">
      <div class="incall-origin-ticket__intro">
        <span class="incall-origin-ticket__badge">
          <i class="pi pi-eye"></i>
          Ticket di apertura
        </span>
        <h2 class="incall-origin-ticket__title">
          #{{ ticket.id_ticket }}
          <span v-if="ticket.area" class="incall-origin-ticket__area">— {{ ticket.area }}</span>
        </h2>
        <p class="incall-origin-ticket__subject">{{ ticket.title || ticket.titolo || 'Senza titolo' }}</p>
        <div class="incall-origin-ticket__meta">
          <span
            v-if="ticket.status || ticket.stato"
            class="incall-origin-ticket__status"
            :style="ticket.color ? { '--status-color': ticket.color } : null"
          >
            <span class="incall-origin-ticket__status-dot"></span>
            {{ ticket.status || ticket.stato }}
          </span>
          <span v-if="ticket.creator || ticket.creator_name">
            {{ ticket.creator || ticket.creator_name }}
            <template v-if="ticket.creator_type">({{ ticket.creator_type }})</template>
          </span>
        </div>
      </div>

      <div class="incall-origin-ticket__actions">
        <button type="button" class="incall-origin-ticket__btn" @click="$emit('view', ticket)">
          <i class="pi pi-eye"></i>
          Visualizza
        </button>
        <button type="button" class="incall-origin-ticket__btn incall-origin-ticket__btn--primary" @click="$emit('reply', ticket)">
          <i class="pi pi-reply"></i>
          Rispondi
        </button>
        <button
          type="button"
          class="incall-origin-ticket__dismiss"
          title="Nascondi"
          @click="$emit('dismiss')"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
    </header>

    <div class="incall-origin-ticket__body">
      <div v-if="loading" class="incall-origin-ticket__loading">
        <i class="pi pi-spin pi-spinner"></i>
        Caricamento conversazione...
      </div>
      <div v-else-if="flatMessages.length === 0" class="incall-origin-ticket__empty">
        Nessun messaggio nella conversazione
      </div>
      <div v-else class="incall-origin-ticket__messages">
        <div
          v-for="message in flatMessages"
          :key="message.id_ticket_message || message._key"
          class="incall-origin-ticket__message"
        >
          <div class="incall-origin-ticket__message-meta">
            {{ formatMessageTime(message) }}
            <template v-if="message.sender"> · {{ message.sender }}</template>
          </div>
          <div class="incall-origin-ticket__message-content" v-html="message.content"></div>
          <div v-if="messageAttachments(message).length" class="incall-origin-ticket__attachments">
            <div
              v-for="(attachment, attachmentIndex) in messageAttachments(message)"
              :key="`${attachmentKey(attachment)}-${attachmentIndex}`"
              class="incall-origin-ticket__attachment"
            >
              <span class="incall-origin-ticket__attachment-name">
                <i class="pi pi-file"></i>
                {{ attachment.name || 'Allegato' }}
              </span>
              <span class="incall-origin-ticket__attachment-actions">
                <button type="button" title="Apri allegato" @click="openAttachment(attachment)">
                  <i class="pi pi-eye"></i>
                </button>
                <button type="button" title="Scarica allegato" @click="downloadAttachment(attachment)">
                  <i class="pi pi-download"></i>
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import TicketService from '@/services/ask-ticket/ticket-service'

export default {
  name: 'IncallOriginTicket',

  props: {
    ticket: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['view', 'reply', 'dismiss'],

  computed: {
    flatMessages() {
      const messages = this.ticket?.messages
      if (!messages) return []

      if (Array.isArray(messages)) {
        return messages.map((message, index) => ({
          ...message,
          _key: message.id_ticket_message || `msg-${index}`,
        }))
      }

      if (typeof messages === 'object') {
        const flat = []
        Object.keys(messages).forEach((dateKey) => {
          const dayMessages = Array.isArray(messages[dateKey]) ? messages[dateKey] : []
          dayMessages.forEach((message, index) => {
            flat.push({
              ...message,
              _key: message.id_ticket_message || `${dateKey}-${index}`,
            })
          })
        })
        return flat
      }

      return []
    },
  },

  data() {
    return {
      ticketService: null,
    }
  },

  mounted() {
    this.ticketService = new TicketService(this.$http, this.$eventBus)
  },

  methods: {
    messageAttachments(message) {
      const list = message?.attachments
      if (Array.isArray(list)) return list
      if (list && typeof list === 'object') return Object.values(list)
      return []
    },

    attachmentKey(attachment) {
      return attachment?.code || attachment?.mail_attachment_id || attachment?.name || attachment?.path
    },

    openAttachment(attachment) {
      this.ticketService?.openAttachment(attachment)
    },

    downloadAttachment(attachment) {
      this.ticketService?.downloadAttachment(attachment)
    },

    formatMessageTime(message) {
      const value = message?.datetime || message?.date
      if (!value) return ''
      const normalized = String(value).includes('T') ? value : String(value).replace(' ', 'T')
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.incall-origin-ticket {
  background: #fff;
  border: 1px solid rgba(13, 60, 97, 0.12);
  border-left: 4px solid #1a5490;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(13, 60, 97, 0.08);
  margin-bottom: 2rem;
  overflow: hidden;
}

.incall-origin-ticket__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  background: linear-gradient(135deg, rgba(13, 60, 97, 0.06), rgba(26, 84, 144, 0.02));
  border-bottom: 1px solid rgba(13, 60, 97, 0.08);
}

.incall-origin-ticket__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #e8f1fb;
  color: #0d3c61;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.incall-origin-ticket .pi {
  font-family: 'primeicons' !important;
  font-style: normal !important;
  font-weight: normal !important;
  line-height: 1;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.incall-origin-ticket__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
}

.incall-origin-ticket__area {
  font-weight: 600;
  color: #64748b;
}

.incall-origin-ticket__subject {
  margin: 0.35rem 0 0.55rem;
  font-size: 1rem;
  font-weight: 600;
  color: #334155;
}

.incall-origin-ticket__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #64748b;
}

.incall-origin-ticket__status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  color: var(--status-color, #0d3c61);
}

.incall-origin-ticket__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--status-color, #0d3c61);
}

.incall-origin-ticket__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.incall-origin-ticket__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.8rem;
  border: 1px solid #c5d4e4;
  border-radius: 8px;
  background: #fff;
  color: #0d3c61;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.incall-origin-ticket__btn:hover {
  background: #f3f7fb;
}

.incall-origin-ticket__btn--primary {
  border-color: #1a5490;
  background: #1a5490;
  color: #fff;
}

.incall-origin-ticket__btn--primary:hover {
  background: #0d3c61;
}

.incall-origin-ticket__dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
}

.incall-origin-ticket__dismiss:hover {
  background: #f1f5f9;
  color: #475569;
}

.incall-origin-ticket__body {
  padding: 1rem 1.25rem 1.15rem;
}

.incall-origin-ticket__loading,
.incall-origin-ticket__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 72px;
  color: #64748b;
  font-size: 0.9rem;
}

.incall-origin-ticket__messages {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.incall-origin-ticket__message {
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e8eef5;
}

.incall-origin-ticket__message-meta {
  margin-bottom: 0.35rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.incall-origin-ticket__message-content {
  font-size: 0.92rem;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.incall-origin-ticket__attachments {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.6rem;
}

.incall-origin-ticket__attachment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e1e8ef;
}

.incall-origin-ticket__attachment-name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  font-size: 0.82rem;
  color: #1f2937;
  word-break: break-word;
}

.incall-origin-ticket__attachment-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
}

.incall-origin-ticket__attachment-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #0d3c61;
  cursor: pointer;
}

.incall-origin-ticket__attachment-actions button:hover {
  background: #e4eef6;
}

.incall-origin-ticket__message-content :deep(p) {
  margin: 0 0 0.4rem;
}

.incall-origin-ticket__message-content :deep(p:last-child) {
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .incall-origin-ticket__header {
    flex-direction: column;
  }

  .incall-origin-ticket__actions {
    width: 100%;
  }
}
</style>
